/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import { useState, useEffect } from 'react';
import apiClient from '../api/client';

const DAISY_THEMES = [
  'light', 'dark', 'cupcake', 'bumblebee', 'emerald', 'corporate', 'synthwave',
  'retro', 'cyberpunk', 'valentine', 'halloween', 'garden', 'forest', 'aqua',
  'lofi', 'pastel', 'fantasy', 'wireframe', 'black', 'luxury', 'dracula',
  'cmyk', 'autumn', 'business', 'acid', 'lemonade', 'night', 'coffee', 'winter',
  'dim', 'nord', 'sunset', 'caramellatte',
];
const VALID_THEMES = new Set(DAISY_THEMES);

const SESSION_KEY = 'pre_login_branding';

/**
 * Detects the active sub-account slug from:
 *   1. ?subdomain=<slug> query param (for local dev testing)
 *   2. The first subdomain segment of window.location.hostname
 *      (e.g. "acme" from "acme.send2me.com")
 *
 * Returns null when no slug is found (default branding applies).
 */
function detectSlug() {
  // 1. Query param — dev/testing convenience
  const params = new URLSearchParams(window.location.search);
  const qpSlug = params.get('subdomain');
  if (qpSlug && qpSlug.trim()) return qpSlug.trim().toLowerCase();

  // 2. Actual subdomain based on environment variable
  const baseHost = process.env.REACT_APP_BASE_URL_HOSTNAME;
  if (baseHost) {
    const currentHost = window.location.hostname;

    if (currentHost.endsWith('.' + baseHost)) {
      const slug = currentHost.substring(0, currentHost.length - (baseHost.length + 1));
      if (slug && slug !== 'www') {
        return slug.toLowerCase();
      }
    }
    return null;
  }

  // 3. Fallback if env variable is not set
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  for(let i = 0; i < parts.length; i++)
  {
    if(parts[i].includes("send2me")) {
      return parts[i-1] ? parts[i-1].toLowerCase() : null;
    }
  }
  return null;
}

function validateTheme(theme) {
  if (theme && VALID_THEMES.has(theme)) return theme;
  return 'light';
}

function validateLogoUrl(url) {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return null;
}

function applyFavicon(faviconUrl) {
  const url = faviconUrl || '/favicon.ico';
  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = url;
}

/**
 * Pre-login branding hook.
 *
 * With an `accountSlug` (the /login/:accountSlug page) it fetches the account's
 * branding from GET /public/branding/account?slug=<accountSlug>, which also says
 * which sign-in method the page must use (`authProvider`). Otherwise it detects
 * the active sub-account slug and fetches GET /public/branding?slug=<slug>
 * (sub-account branding, falling back to the account per field).
 *
 * Applies the theme to the document and returns
 * { logoUrl, faviconUrl, theme, name, slug, accountSlug, authProvider, loading }.
 *
 * Results are cached in sessionStorage so a page refresh (e.g. after
 * password reset redirect) doesn't trigger an extra network round-trip.
 */
export function useBranding(accountSlug = null) {
  const slug = accountSlug ? null : detectSlug();
  const brandingUrl = accountSlug
    ? `/public/branding/account?slug=${encodeURIComponent(accountSlug)}`
    : slug && `/public/branding?slug=${encodeURIComponent(slug)}`;

  const [branding, setBranding] = useState({
    logoUrl: null, faviconUrl: null, theme: 'light', name: null,
    slug: slug ?? null, accountSlug, authProvider: 'local',
  });

  const [loading, setLoading] = useState(!!brandingUrl);

  useEffect(() => {
    if (!brandingUrl) {
      // No subdomain — apply default theme in case a previous session left one
      document.documentElement.setAttribute('data-theme', 'light');
      applyFavicon(null);
      setLoading(false);
      return;
    }

    // Fetch from backend
    const fetchBranding = async () => {
      try {
        const { data } = await apiClient.get(brandingUrl);
        const logoUrl = validateLogoUrl(data.logoUrl);
        const faviconUrl = validateLogoUrl(data.faviconUrl);
        const theme = validateTheme(data.theme);
        const name = data.name ?? null;
        const authProvider = data.authProvider === 'pixo' ? 'pixo' : 'local';

        const result = { logoUrl, faviconUrl, theme, name, slug, accountSlug, authProvider };

        // Apply theme and favicon immediately
        document.documentElement.setAttribute('data-theme', theme);
        applyFavicon(faviconUrl);

        // Cache in sessionStorage
        try {
          sessionStorage.setItem(SESSION_KEY, JSON.stringify(result));
        } catch {
          // quota or private mode — non-fatal
        }

        setBranding(result);
      } catch {
        // Network or server error — fall back to defaults silently
        document.documentElement.setAttribute('data-theme', 'light');
        applyFavicon(null);
        setBranding({ logoUrl: null, faviconUrl: null, theme: 'light', name: null, slug, accountSlug, authProvider: 'local' });
      } finally {
        setLoading(false);
      }
    };

    fetchBranding();
  }, [brandingUrl, slug, accountSlug]);

  return { ...branding, loading };
}
